import { forwardRef, useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { BORDER_RADIUS } from "../../utils/constants";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "./chart";
import {
  CartesianGrid,
  LabelList,
  Line,
  LineChart as LineChartLib,
  XAxis,
  YAxis,
  ReferenceDot,
  ReferenceLine,
} from "recharts";
import { useTranslationApp } from "../../lib/i18next";
import {
  DATE_SHORT_2,
  OPTIMIZATION,
  VALUE_NUMBER,
  VALUE_WITH_CURRENCY_MANUAL_FORMAT,
} from "../../i18n/keysTranslations";
import {
  BACKGROUND_COLOR_OPTIMIZATION,
  BOX_SHADOW_THEME,
  INPUT_COLOR_BORDER,
  TEXT_COLOR_OPTIMIZATION,
} from "../../utils/colors";
import { numberDecimal } from "../../utils/numbers";

const METRICS_PREVIOUS = [
  "impressionsPrevious",
  "clicksPrevious",
  "mainActionsPrevious",
  "metaPrevious",
  "tiktokPrevious",
  "googlePrevious",
];

const LineChart = forwardRef((props, ref) => {
  const {
    chartData = {},
    chartConfig = {},
    xAxisKey = "label",
    title,
    subtitle,
    description,
    hasTooltip = true,
    hasLegend = true,
    hasCartesianGrid = true,
    hasYAxis = false,
    valueFormat,
    sx = {},
    height = "unset",
    customComponentDetail = null,
    optimizationLogs = [],
    yAxisDomain,
    pdfCaptureId,
    referenceLines = [],
    stacked = true,
  } = props;
  const { t } = useTranslationApp();

  // State for custom optimization tooltip
  const [optimizationTooltip, setOptimizationTooltip] = useState({
    show: false,
    logs: [],
    position: { x: 0, y: 0 },
  });

  // State for capturing the global mouse position
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Effect for capturing the global mouse position
  useEffect(() => {
    const onMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    document.addEventListener("mousemove", onMouseMove);
    return () => document.removeEventListener("mousemove", onMouseMove);
  }, []);

  // Functions for handling the optimization tooltip
  const onOptimizationMouseEnter = (event, logs) => {
    setOptimizationTooltip({
      show: true,
      logs: logs,
      position: { x: mousePosition.x, y: mousePosition.y },
    });
  };

  const onOptimizationMouseLeave = () => {
    setOptimizationTooltip({
      show: false,
      logs: [],
      position: { x: 0, y: 0 },
    });
  };

  const chartConfigKeys = Object.keys(chartConfig);
  const hasMoreSix = chartData.length > 6;

  // Helper functions for date parsing and comparison
  const createDateFromDayMonth = (day, monthStr, referenceYear) => {
    const dateString = `${day} ${monthStr} ${referenceYear}`;
    const parsedDate = new Date(dateString);
    return isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const parseDate = (input, referenceYear, useRegex = false) => {
    let day, monthStr;

    if (useRegex) {
      // Parse using regex for labels like "15 Mar" within complex strings
      const labelMatch = input.match(/(\d{1,2})\s+(\w{3})/);
      if (!labelMatch) return null;

      day = parseInt(labelMatch[1]);
      monthStr = labelMatch[2];
    } else {
      // Parse using split for simple date strings like "11 Nov"
      const parts = input.trim().split(" ");
      if (parts.length !== 2) return null;

      day = parseInt(parts[0]);
      monthStr = parts[1];
    }

    return createDateFromDayMonth(day, monthStr, referenceYear);
  };

  const isLogDateInSingleDay = (logDate, pointDate) => {
    return logDate.toDateString() === pointDate.toDateString();
  };

  const isLogDateInDateRange = (logDate, startDate, endDate) => {
    // Set end date to end of day for inclusive comparison
    const endOfDay = new Date(endDate);
    endOfDay.setHours(23, 59, 59, 999);

    // Set log date to start of day for comparison
    const startOfLogDay = new Date(logDate);
    startOfLogDay.setHours(0, 0, 0, 0);

    return startOfLogDay >= startDate && startOfLogDay <= endOfDay;
  };

  const isLogMatchingDataPoint = (log, dataPoint) => {
    const logDate = new Date(log.creationTime);
    const currentYear = logDate.getFullYear();
    const labelToolTip = dataPoint.labelToolTip || "";

    // Case 1: Single day comparison (no labelToolTip)
    if (!labelToolTip) {
      const pointLabel = dataPoint.label || "";
      const pointDate = parseDate(pointLabel, currentYear, true);
      return pointDate ? isLogDateInSingleDay(logDate, pointDate) : false;
    }

    // Case 2: Date range comparison (with labelToolTip)
    const dateRangeParts = labelToolTip.split(" - ");
    if (dateRangeParts.length !== 2) return false;

    const [startDateStr, endDateStr] = dateRangeParts;
    const startDate = parseDate(startDateStr, currentYear);
    const endDate = parseDate(endDateStr, currentYear);

    if (!startDate || !endDate) return false;

    return isLogDateInDateRange(logDate, startDate, endDate);
  };

  // Main function to get optimization logs for a specific data point and platform
  const getOptimizationLogsForDataPoint = (dataPoint, platform) => {
    if (!optimizationLogs || optimizationLogs.length === 0) {
      return [];
    }

    return optimizationLogs.filter((log) => {
      return (
        log.platform === platform && isLogMatchingDataPoint(log, dataPoint)
      );
    });
  };

  const getValueFormatted = (value, name) => {
    if (chartConfig?.[name]?.valueFormatter) {
      return chartConfig?.[name]?.valueFormatter(value);
    }

    const currency = chartConfig?.[name]?.currency;
    if (currency) {
      // For currencies, keep decimals if they are between 0 and 100
      if (value >= 0 && value <= 100 && value % 1 !== 0) {
        return `$${numberDecimal(value, 2)} ${currency}`;
      }
      return t(VALUE_WITH_CURRENCY_MANUAL_FORMAT, {
        value,
        currency,
      });
    }

    if (valueFormat) {
      return t(valueFormat.translation, {
        [valueFormat.key]: value,
        ...valueFormat.options,
      });
    }

    // For normal numbers, keep decimals if they are between 0 and 100
    if (value >= 0 && value <= 100 && value % 1 !== 0) {
      return numberDecimal(value);
    }

    return t(VALUE_NUMBER, {
      value,
    });
  };

  return (
    <Box
      ref={ref}
      data-pdf-capture-id={pdfCaptureId}
      sx={{
        padding: 3,
        borderRadius: BORDER_RADIUS,
        backgroundColor: "fields.background",
        boxShadow: BOX_SHADOW_THEME,
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          {title && <Typography variant="body1">{title}</Typography>}
          {subtitle && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 2,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {customComponentDetail && customComponentDetail}
      </Box>
      <ChartContainer
        config={chartConfig}
        className={`aspect-auto h-[${
          height === "unset" ? "unset" : `${height}px`
        }] w-full`}
      >
        <LineChartLib
          accessibilityLayer
          data={chartData}
          margin={{
            top: 20,
            left: 12,
            right: 25,
            bottom: 12,
          }}
          connectNulls={true}
        >
          {hasCartesianGrid && <CartesianGrid vertical={false} />}
          {hasTooltip && (
            <ChartTooltip
              cursor={false}
              className="bg-slate-50"
              content={(props) => {
                if (!props.active || !props.payload) {
                  return null;
                }

                // Filter payload to only show visible lines and their previous periods
                const filteredPayload = props.payload.filter((item) => {
                  const key = item.dataKey || item.name;

                  // Check if the line itself is visible
                  const isVisible = chartConfig[key]?.isVisible !== false;

                  // If it's a previous period line, check if its corresponding current line is visible
                  if (key.endsWith("Previous")) {
                    const baseKey = key.replace("Previous", "");
                    return chartConfig[baseKey]?.isVisible !== false;
                  }

                  return isVisible;
                });

                if (filteredPayload.length === 0) {
                  return null;
                }

                return (
                  <ChartTooltipContent
                    {...props}
                    payload={filteredPayload}
                    formatter={(value, name, itemProps) => {
                      const growthKey = `${name}Growth`;
                      const growthValue = itemProps.payload[growthKey];
                      const logsForPoint = getOptimizationLogsForDataPoint(
                        itemProps.payload,
                        name
                      );

                      return (
                        <>
                          <div
                            className="h-2.5 w-2.5 shrink-0 rounded-[2px] bg-[--color-bg]"
                            style={{
                              "--color-bg": `var(--color-${name})`,
                            }}
                          />
                          {chartConfig[name]?.label || name}
                          {logsForPoint.length > 0 && (
                            <span
                              style={{
                                marginLeft: 8,
                                backgroundColor: BACKGROUND_COLOR_OPTIMIZATION,
                                color: TEXT_COLOR_OPTIMIZATION,
                                padding: "2px 8px",
                                borderRadius: "12px",
                                display: "inline-flex",
                                alignItems: "center",
                                fontSize: "11px",
                                fontWeight: "bold",
                              }}
                            >
                              {t(OPTIMIZATION)}
                            </span>
                          )}
                          {!METRICS_PREVIOUS.includes(name) && (
                            <span
                              style={{
                                marginLeft: 8,
                                backgroundColor:
                                  growthValue > 0
                                    ? "#e6f4ea"
                                    : growthValue < 0
                                      ? "#fce8e6"
                                      : "#f1f3f4",
                                color:
                                  growthValue > 0
                                    ? "#137333"
                                    : growthValue < 0
                                      ? "#a50e0e"
                                      : "#5f6368",
                                padding: "2px 8px",
                                borderRadius: "12px",
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              {growthValue !== undefined ? (
                                growthValue === 0 ? (
                                  value > 0 ? (
                                    <span style={{ color: "#137333" }}>
                                      ↑ 100%
                                    </span>
                                  ) : (
                                    <span style={{ color: "#5f6368" }}>0%</span>
                                  )
                                ) : (
                                  <>
                                    {growthValue > 0 ? "↑ " : "↓ "}
                                    {Math.abs(growthValue).toFixed(1)}%
                                  </>
                                )
                              ) : value > 0 ? (
                                <span style={{ color: "#137333" }}>↑ 100%</span>
                              ) : (
                                <span style={{ color: "#5f6368" }}>0%</span>
                              )}
                            </span>
                          )}
                          <div className="ml-auto flex items-baseline gap-0.5 font-mono font-medium tabular-nums text-foreground">
                            {getValueFormatted(value, name)}
                          </div>
                        </>
                      );
                    }}
                  />
                );
              }}
            />
          )}
          {hasLegend && <ChartLegend content={<ChartLegendContent />} />}
          <XAxis
            dataKey={xAxisKey}
            tickLine={false}
            axisLine={true}
            tickMargin={8}
            interval="preserveStartEnd"
            allowDataOverflow={false}
          />
          {hasYAxis && (
            <YAxis
              tickLine={false}
              axisLine={true}
              tickMargin={8}
              domain={yAxisDomain || [0, "dataMax"]}
              allowDataOverflow={yAxisDomain ? true : false}
              tickFormatter={(value) => {
                if (typeof value === "number") {
                  return numberDecimal(value);
                }
                return value;
              }}
            />
          )}
          {chartConfigKeys.map((key) => {
            const isVisible = chartConfig[key]?.isVisible !== false;
            return (
              <Line
                key={key}
                dataKey={key}
                type="monotone"
                fill={`var(--color-${key})`}
                fillOpacity={isVisible ? 0.4 : 0}
                stroke={`var(--color-${key})`}
                strokeOpacity={isVisible ? 1 : 0}
                strokeDasharray={chartConfig[key]?.strokeDasharray}
                stackId={stacked ? chartConfig[key]?.stackId || "a" : undefined}
                connectNulls={true}
                isAnimationActive={false}
                hide={!isVisible}
                dot={isVisible ? { r: 4 } : false}
                activeDot={
                  isVisible
                    ? {
                        r: 6,
                        fill: `var(--color-${key})`,
                        stroke: `var(--color-${key})`,
                        strokeWidth: 2,
                      }
                    : false
                }
              >
                {chartConfig[key].hasLabelList && !hasMoreSix && isVisible && (
                  <LabelList
                    position="top"
                    offset={8}
                    className="fill-foreground"
                    fontSize={12}
                    formatter={(value) => getValueFormatted(value, key)}
                  />
                )}
              </Line>
            );
          })}
          {referenceLines.map((lineConfig, index) => (
            <ReferenceLine
              key={`reference-line-${index}`}
              y={lineConfig.y}
              stroke={lineConfig.stroke || INPUT_COLOR_BORDER}
              strokeDasharray={lineConfig.strokeDasharray || "4 4"}
              label={
                lineConfig.label
                  ? {
                      value: lineConfig.label,
                      position: "insideTopRight",
                      fill: "currentColor",
                      fontSize: 11,
                    }
                  : undefined
              }
            />
          ))}
          {/* Optimization points */}
          {chartData.map((dataPoint, index) => {
            return chartConfigKeys.map((platform) => {
              const isVisible = chartConfig[platform]?.isVisible !== false;
              const logsForPoint = getOptimizationLogsForDataPoint(
                dataPoint,
                platform
              );

              // Allow showing points even when the value is 0, if there are optimization logs
              if (
                !isVisible ||
                logsForPoint.length === 0 ||
                dataPoint[platform] === undefined ||
                dataPoint[platform] === null
              ) {
                return null;
              }

              // Use the actual data point value, no artificial positioning
              const yPosition = dataPoint[platform];

              return (
                <ReferenceDot
                  key={`${platform}-${index}-optimization`}
                  x={dataPoint[xAxisKey]}
                  y={yPosition}
                  r={8}
                  fill={`var(--color-${platform})`}
                  stroke={`var(--color-${platform})`}
                  strokeWidth={2}
                  fillOpacity={0.9}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={(event) =>
                    onOptimizationMouseEnter(event, logsForPoint)
                  }
                  onMouseLeave={onOptimizationMouseLeave}
                />
              );
            });
          })}
        </LineChartLib>
      </ChartContainer>

      {/* Custom tooltip for optimization points */}
      {optimizationTooltip.show && (
        <Box
          sx={{
            position: "fixed",
            left: optimizationTooltip.position.x + 10,
            top: optimizationTooltip.position.y - 50,
            backgroundColor: "background.paper",
            border: `2px solid ${INPUT_COLOR_BORDER}`,
            borderRadius: 1,
            padding: 2,
            boxShadow: BOX_SHADOW_THEME,
            maxWidth: 300,
            zIndex: 9999,
            pointerEvents: "none",
          }}
        >
          <Typography
            variant="body2"
            fontWeight="bold"
            sx={{ mb: 1, color: TEXT_COLOR_OPTIMIZATION }}
          >
            {t(OPTIMIZATION)}
          </Typography>
          {optimizationTooltip.logs.map((log, idx) => (
            <Box
              key={idx}
              sx={{ mb: idx < optimizationTooltip.logs.length - 1 ? 1 : 0 }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: "medium", mb: 0.5 }}
              >
                {log.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t(DATE_SHORT_2, {
                  date: new Date(log.creationTime),
                })}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {description && (
        <Typography
          variant="body2"
          sx={{
            mt: 2,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
});

LineChart.displayName = "LineChart";
export default LineChart;
